import {
  MY_POSITIONS_HARVESTER_STATUSES,
  MY_POSITIONS_STAKER_OWNER_TOKEN_IDS,
  POOLS_MY_POSITIONS,
  STAKER_DEPOSITS_NOT_HARVESTED,
  STAKER_DEPOSIT_OWNER
} from './positions.query';
import type {
  HarvestStatusResponse,
  PoolsMyPositionsResponse,
  StakerDepositsNotHarvestedResponse,
  StakerOwnerTokenIdsResponse,
  StakerPositionOwnerResponse
} from './positions.types';
import { useQuery } from '@apollo/client';
import { BigNumber } from '@ethersproject/bignumber';

import { getCorrectPoolChainId } from 'utils/getCorrectPoolChainId';

import { useWeb3React } from 'hooks/useWeb3React';

import { useGraphClients } from 'graphql/thegraph/useGraphClients';

type useHarvesterStatusesProps = {
  chainId: number;
  ids: string[];
};

export const useHarvesterStatuses = ({
  chainId,
  ids
}: useHarvesterStatusesProps) => {
  const { harvesterClient } = useGraphClients(chainId);

  const { data, error, loading, refetch } = useQuery<HarvestStatusResponse>(
    MY_POSITIONS_HARVESTER_STATUSES({ ids }),
    {
      variables: { ids },
      client: harvesterClient,
      fetchPolicy: 'no-cache'
    }
  );

  return { data, error, loading, refetch };
};

type useStakerOwnerTokenIdsProps = {
  chainId: number;
  address?: string;
};

export const useStakerOwnerTokenIds = ({
  chainId,
  address
}: useStakerOwnerTokenIdsProps) => {
  const { stakerClient } = useGraphClients(chainId);

  const owner = address ? (address as string).toLowerCase() : '';

  const {
    data: stakerOwnerTokenIds,
    error,
    loading
  } = useQuery<StakerOwnerTokenIdsResponse>(
    MY_POSITIONS_STAKER_OWNER_TOKEN_IDS({
      owner
    }),
    {
      variables: { owner },
      client: stakerClient,
      fetchPolicy: 'no-cache',
      skip: !address
    }
  );

  const data = stakerOwnerTokenIds?.deposits?.map((position) => position.id);

  return { data, error, loading };
};

type useStakerPositionsProps = {
  chainId: number;
};

export const useStakerPositions = ({ chainId }: useStakerPositionsProps) => {
  const { account } = useWeb3React();

  const { stakerClient } = useGraphClients(chainId);

  const {
    data: stakerDepositsNotHarvestedResponse,
    error,
    loading
  } = useQuery<StakerDepositsNotHarvestedResponse>(
    STAKER_DEPOSITS_NOT_HARVESTED({
      owner: account?.toLowerCase() || ''
    }),
    {
      variables: { owner: account },
      client: stakerClient,
      fetchPolicy: 'no-cache',
      skip: !account
    }
  );

  const data = stakerDepositsNotHarvestedResponse?.deposits.map(
    (deposit) => deposit.id
  );

  return { data, loading, error };
};

const extractActivePools = ({ pools }: PoolsMyPositionsResponse) =>
  pools.reduce((activeIds, pool) => {
    const totalMinted = pool.mints.reduce(
      (sum, mint) => sum.add(BigNumber.from(mint.amount)),
      BigNumber.from(0)
    );
    const totalBurned = pool.burns.reduce(
      (sum, burn) => sum.add(BigNumber.from(burn.amount)),
      BigNumber.from(0)
    );
    const netLiquidity = totalMinted.sub(totalBurned);

    if (netLiquidity.gt(0)) {
      activeIds.push(pool.id);
    }

    return activeIds;
  }, [] as string[]);

export const useMyPositionIds = () => {
  const { account, chainId } = useWeb3React();

  const correctChainId = getCorrectPoolChainId({ chainId });

  const { dataClient } = useGraphClients(correctChainId);

  const owner = account ? (account as string).toLowerCase() : '';

  const {
    data: myPositions,
    error,
    loading
  } = useQuery<PoolsMyPositionsResponse>(
    POOLS_MY_POSITIONS({
      origin: owner
    }),
    {
      variables: { origin: owner },
      client: dataClient,
      fetchPolicy: 'no-cache',
      skip: !account
    }
  );

  const activePools = myPositions
    ? extractActivePools({ pools: myPositions?.pools })
    : [];

  return { data: activePools, loading, error };
};

type useStakerPositionOwnerProps = {
  chainId: number;
  positionId: string;
};

export const useStakerPositionOwner = ({
  chainId,
  positionId
}: useStakerPositionOwnerProps) => {
  const { account } = useWeb3React();

  const { stakerClient } = useGraphClients(chainId);

  const {
    data: stakerDepositOwnerResponse,
    error,
    loading
  } = useQuery<StakerPositionOwnerResponse>(
    STAKER_DEPOSIT_OWNER({
      positionId
    }),
    {
      variables: { positionId },
      client: stakerClient,
      fetchPolicy: 'no-cache',
      skip: !account
    }
  );

  const data = stakerDepositOwnerResponse &&
    stakerDepositOwnerResponse.deposit && {
      owner: stakerDepositOwnerResponse.deposit.owner,
      isAutoHarvested: stakerDepositOwnerResponse.deposit.isAutoHarvested
    };

  return { data, loading, error };
};
